// OpsRequestForm — the "🛠 Request from Ops" modal (Sep 25, 2026).
//
// Opened from a deal page (job pre-filled) or from the Requests page (job
// picked by typeahead first). The rep picks a category, says what they need,
// optionally sets a needed-by date, attaches photos/files, and flags Urgent.
// The request goes to whoever holds the Roof Team Manager role — the database
// resolves that, the rep never picks a person.
//
// Attachments upload AFTER the row exists (they're tagged to the request id).
// A failed upload never loses the request: the modal stays open, names the
// file that failed, and offers Done.

import { useEffect, useMemo, useRef, useState } from "react";
import AttachmentPicker from "../shared/AttachmentPicker";
import { describeFailures, fmtBytes, type AttachmentFailure } from "../../lib/warrantyAttachments";
import {
  CATEGORIES, fileRequest, searchDeals, uploadRequestAttachments,
  type DealSearchHit, type OpsCategory, type OpsPriority, type OpsRequest,
} from "../../lib/opsRequests";

export interface OpsFormDeal {
  id: string;
  homeowner_name: string | null;
  property_street?: string | null;
  property_city?: string | null;
  stage?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Fires once the row is in; attachments may still be uploading. */
  onFiled?: (req: OpsRequest) => void;
  /** Pre-filled job (deal page). Omit to show the job search first. */
  deal?: OpsFormDeal | null;
  actorId: string;
}

const MAX_BODY = 4000;

function prettyStage(s: string | null | undefined): string {
  return (s ?? "").replace(/_/g, " ");
}

export default function OpsRequestForm({ open, onClose, onFiled, deal, actorId }: Props) {
  const [job, setJob] = useState<OpsFormDeal | null>(deal ?? null);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<DealSearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [category, setCategory] = useState<OpsCategory | null>(null);
  const [priority, setPriority] = useState<OpsPriority>("normal");
  const [body, setBody] = useState("");
  const [neededBy, setNeededBy] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sentReq, setSentReq] = useState<OpsRequest | null>(null);
  const [uploadFailures, setUploadFailures] = useState<AttachmentFailure[]>([]);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  // Reset on open.
  useEffect(() => {
    if (!open) return;
    setJob(deal ?? null); setQ(""); setHits([]); setCategory(null); setPriority("normal");
    setBody(""); setNeededBy(""); setFiles([]); setBusy(false); setProgress(null);
    setErr(null); setSentReq(null); setUploadFailures([]);
  }, [open, deal]);

  // ESC closes (unless mid-send).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  // Job typeahead (Requests-page path only).
  useEffect(() => {
    if (!open || job || q.trim().length < 2) { setHits([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const rows = await searchDeals(q);
        if (!cancelled) setHits(rows);
      } catch {
        if (!cancelled) setHits([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, job, open]);

  const jobLine = useMemo(() => {
    if (!job) return "";
    const addr = [job.property_street, job.property_city].filter(Boolean).join(", ");
    return [job.homeowner_name || "(no name)", addr].filter(Boolean).join(" · ");
  }, [job]);

  const canSend = !!job && !!category && body.trim().length > 0 && !busy;

  async function send() {
    if (!job || !category) return;
    setBusy(true); setErr(null);
    try {
      const req = await fileRequest({
        dealId: job.id, requestedBy: actorId, category, priority, body, neededBy: neededBy || null,
      });
      onFiled?.(req);
      if (files.length) {
        setProgress({ done: 0, total: files.length });
        const res = await uploadRequestAttachments({
          dealId: job.id, requestId: req.id, files, uploaderId: actorId,
          onProgress: (done, total) => setProgress({ done, total }),
        });
        if (res.failures.length) {
          setSentReq(req); setUploadFailures(res.failures); setBusy(false);
          return; // stay open so the failure is seen
        }
      }
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't send the request.");
    } finally {
      setBusy(false); setProgress(null);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
         onClick={() => { if (!busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="ops-form-title"
           className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <h2 id="ops-form-title" className="font-bold text-bardin-ink">🛠 Request from Ops</h2>
          <button type="button" onClick={onClose} disabled={busy}
                  className="text-slate-400 hover:text-slate-600 text-xl leading-none px-1" aria-label="Close">×</button>
        </div>

        {sentReq ? (
          <div className="p-4 space-y-3">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800">
              ✓ Request sent. Robert has it.
            </div>
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-800">
              <span className="font-semibold">{uploadFailures.length} file{uploadFailures.length === 1 ? "" : "s"} didn't upload:</span>{" "}
              {describeFailures(uploadFailures)}. You can add them from the request's thread later.
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={onClose}
                      className="px-4 py-2 rounded-lg bg-bardin-ink text-white text-sm font-semibold">Done</button>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Job */}
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Job</div>
              {job ? (
                <div className="flex items-start justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-bardin-ink truncate">{jobLine}</div>
                    {job.stage && <div className="text-xs text-slate-500 capitalize">{prettyStage(job.stage)}</div>}
                  </div>
                  {!deal && (
                    <button type="button" onClick={() => { setJob(null); setQ(""); }}
                            className="text-xs text-bardin-teal font-semibold shrink-0">Change</button>
                  )}
                </div>
              ) : (
                <div>
                  <input
                    id="ops-form-job-search"
                    type="search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search homeowner or street…"
                    autoFocus
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  {q.trim().length >= 2 && (
                    <div className="mt-1 rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-56 overflow-y-auto">
                      {searching && hits.length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>
                      )}
                      {!searching && hits.length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-500">
                          No job matches. If it isn't in the CRM yet, log the lead first (home page → Log a Lead).
                        </div>
                      )}
                      {hits.map((h) => (
                        <button key={h.id} type="button"
                                onClick={() => { setJob(h); setTimeout(() => bodyRef.current?.focus(), 50); }}
                                className="w-full text-left px-3 py-2 hover:bg-orange-50">
                          <div className="text-sm font-semibold text-bardin-ink">{h.homeowner_name || "(no name)"}</div>
                          <div className="text-xs text-slate-500">
                            {[h.property_street, h.property_city].filter(Boolean).join(", ")}
                            {h.stage ? ` · ${prettyStage(h.stage)}` : ""}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Category */}
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">What is it about?</div>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map((c) => (
                  <button key={c.key} type="button" onClick={() => setCategory(c.key)}
                          aria-pressed={category === c.key}
                          className={`px-2.5 py-1.5 rounded-full text-xs font-semibold border transition ${
                            category === c.key
                              ? "bg-bardin-ink text-white border-bardin-ink"
                              : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                    {c.icon} {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Body */}
            <div>
              <label htmlFor="ops-form-body" className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                What do you need Robert to do?
              </label>
              <textarea
                id="ops-form-body"
                ref={bodyRef}
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
                rows={4}
                placeholder="Be specific — who, what, when. Anything Robert would otherwise have to call you to ask."
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>

            {/* Needed by + priority */}
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label htmlFor="ops-form-needed-by" className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                  Needed by <span className="normal-case font-normal text-slate-400">(optional)</span>
                </label>
                <input id="ops-form-needed-by" type="date" value={neededBy}
                       onChange={(e) => setNeededBy(e.target.value)}
                       className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Priority</div>
                <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
                  <button type="button" onClick={() => setPriority("normal")} aria-pressed={priority === "normal"}
                          className={`px-3 py-2 text-xs font-semibold ${priority === "normal" ? "bg-slate-100 text-bardin-ink" : "bg-white text-slate-500"}`}>
                    Normal
                  </button>
                  <button type="button" onClick={() => setPriority("urgent")} aria-pressed={priority === "urgent"}
                          className={`px-3 py-2 text-xs font-semibold border-l border-slate-300 ${priority === "urgent" ? "bg-rose-50 text-rose-700" : "bg-white text-slate-500"}`}>
                    Urgent
                  </button>
                </div>
              </div>
            </div>
            {priority === "urgent" && (
              <p className="text-xs text-rose-700 -mt-2">Urgent jumps to the top of Robert's list and his text says URGENT. Use it when today matters.</p>
            )}

            {/* Attachments */}
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Photos & files <span className="normal-case font-normal text-slate-400">(optional)</span></div>
              <AttachmentPicker disabled={busy} onFiles={(picked) => setFiles((prev) => [...prev, ...picked])} />
              {files.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {files.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1">
                      <span className="truncate">📎 {f.name} <span className="text-slate-400">{fmtBytes(f.size)}</span></span>
                      <button type="button" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                              disabled={busy} className="text-slate-400 hover:text-rose-600" aria-label={`Remove ${f.name}`}>✕</button>
                    </li>
                  ))}
                </ul>
              )}
              {progress && (
                <div className="mt-1 text-xs text-slate-500">Uploading {progress.done}/{progress.total}…</div>
              )}
            </div>

            {err && <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-800">{err}</div>}

            <div className="flex items-center justify-between gap-2 pt-1">
              <p className="text-xs text-slate-500">Goes to Robert. He'll text you when it's done.</p>
              <button type="button" onClick={send} disabled={!canSend}
                      className="px-4 py-2 rounded-lg bg-bardin-orange text-white text-sm font-semibold disabled:opacity-50">
                {busy ? "Sending…" : "Send to Robert"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
